import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";
import "../styles/Landing.css";
import MasterEdLogo from "../assets/masteredlogo-removebg-preview.png";
import { TrendingUp, Speed, TrackChanges, EmojiEvents } from '@mui/icons-material';

const Landing = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const phrases = [
    'Achieve Your Dream Score',
    'Master the SAT Exam',
    'Unlock Your Full Potential',
    'Excel in Every Test'
  ];

  useEffect(() => {
    // Check if already logged in
    if (authService.isLoggedIn()) {
      navigate("/dashboard");
    }
    // Trigger animation
    setIsVisible(true);
  }, [navigate]);

  // Typewriter effect
  useEffect(() => {
    const currentPhrase = phrases[phraseIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseDuration = isDeleting ? 500 : 2000;

    if (!isDeleting && typedText === currentPhrase) {
      // Pause before deleting
      setTimeout(() => setIsDeleting(true), pauseDuration);
      return;
    }

    if (isDeleting && typedText === '') {
      // Move to next phrase
      setIsDeleting(false);
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    const timeout = setTimeout(() => {
      setTypedText((prev) => {
        if (isDeleting) {
          return currentPhrase.substring(0, prev.length - 1);
        } else {
          return currentPhrase.substring(0, prev.length + 1);
        }
      });
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [typedText, isDeleting, phraseIndex, phrases]);

  const handleGetStarted = () => {
    navigate("/login");
  };

  return (
    <div className="landing-container">
      {/* Animated Background */}
      <div className="landing-bg">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      </div>

      {/* Content */}
      <div className={`landing-content ${isVisible ? 'visible' : ''}`}>
        {/* Logo Section */}
        <div className="landing-logo-section fade-in-up">
          <img src={MasterEdLogo} alt="Master Education" className="landing-logo" />
          <h1 className="landing-title">Master Education</h1>
          <p className="landing-subtitle">SAT Testing Platform</p>
        </div>

        {/* Hero Text */}
        <div className="landing-hero fade-in-up delay-1">
          <h2 className="hero-heading">
            <span className="gradient-text-modern typewriter">
              {typedText}
              <span className="cursor">|</span>
            </span>
          </h2>
          <p className="hero-description">
            Take SAT tests and excel with our comprehensive platform. 
            Real exam experience, instant feedback, and personalized insights.
          </p>
        </div>

        {/* Features Grid */}
        <div className="landing-features fade-in-up delay-2">
          <div className="feature-card">
            <div className="feature-icon-modern">
              <TrendingUp sx={{ fontSize: 56 }} />
            </div>
            <h3>Track Progress</h3>
            <p>Monitor your performance with detailed analytics</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon-modern">
              <Speed sx={{ fontSize: 56 }} />
            </div>
            <h3>Instant Results</h3>
            <p>Get immediate feedback on every practice test</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon-modern">
              <TrackChanges sx={{ fontSize: 56 }} />
            </div>
            <h3>Targeted Practice</h3>
            <p>Focus on areas that need improvement</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon-modern">
              <EmojiEvents sx={{ fontSize: 56 }} />
            </div>
            <h3>Leaderboards</h3>
            <p>Compete with peers and stay motivated</p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="landing-cta fade-in-up delay-3">
          <button className="cta-button" onClick={handleGetStarted}>
            <span>Log In to Your Account</span>
            <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <p className="cta-hint">For active students only</p>
        </div>

        {/* Floating Stats */}
        <div className="landing-stats fade-in-up delay-4">
          <div className="stat-item">
            <div className="stat-number counter">1500+</div>
            <div className="stat-label">Active Students</div>
          </div>
          <div className="stat-item">
            <div className="stat-number counter">150+</div>
            <div className="stat-label">Study Groups</div>
          </div>
          <div className="stat-item">
            <div className="stat-number counter">10K+</div>
            <div className="stat-label">Tests Taken</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
